import type React from 'react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  cn,
  ColorInput,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  Input,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { OctagonXIcon, RadioIcon, UploadIcon } from './icons';

export type CompanyEditStatus = 'active' | 'inactive';

export interface CompanyEditValues {
  name: string;
  cnpj: string;
  slug: string;
  website: string;
  primaryColor: string;
}

export interface CompanyEditPageProps {
  status: CompanyEditStatus;
  values: CompanyEditValues;
  errors: Record<string, string>;
  logoFileName: string | null;
  /** Either an object URL (when the user picked a fresh logo) or the
   *  stored `logoUrl` from Core. Drives the small avatar next to the title. */
  logoPreviewUrl: string | null;
  isSubmitting: boolean;
  /** Top-level error (non-field) shown above the submit button. */
  submitError?: string | null;
  /** When true (default for the edit container), the Slug input is rendered readonly. */
  slugReadonly?: boolean;
  onFieldChange: (field: keyof CompanyEditValues, value: string) => void;
  onLogoChange: (file: File | null) => void;
  onSubmit: () => void;
  /** Optional extra sections rendered below the main form (e.g. integrations). */
  children?: React.ReactNode;
}

export function CompanyEditPage({
  status,
  values,
  errors,
  logoFileName,
  logoPreviewUrl,
  isSubmitting,
  submitError,
  slugReadonly = false,
  onFieldChange,
  onLogoChange,
  onSubmit,
  children,
}: CompanyEditPageProps) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="home" />

      <main className="flex flex-1 flex-col items-center gap-8 bg-card px-8 pt-8 pb-16">
        <div className="flex w-full max-w-[630px] flex-col gap-8">
          <div className="flex flex-row items-center gap-3">
            {logoPreviewUrl ? (
              <Avatar size="md" shape="square" data-testid="company-form-logo-preview">
                <AvatarImage src={logoPreviewUrl} alt={values.name || 'Logo da empresa'} />
                <AvatarFallback>{(values.name || 'E').charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
            ) : null}
            <h1 className="text-4xl font-medium leading-none tracking-tight text-foreground">
              Edição de empresa
            </h1>
            <StatusBadge status={status} />
          </div>

          <form
            className="flex flex-col gap-5"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
            noValidate
          >
            <FormField error={Boolean(errors.name)}>
              <FormFieldLabel>Nome da empresa</FormFieldLabel>
              <Input
                value={values.name}
                placeholder="Eureca"
                variant={errors.name ? 'destructive' : 'default'}
                onChange={(event) => onFieldChange('name', event.target.value)}
                data-testid="company-form-name"
              />
              <FormFieldError>{errors.name}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.cnpj)}>
              <FormFieldLabel>CNPJ</FormFieldLabel>
              <Input
                value={values.cnpj}
                placeholder="11.222.333/4444-55"
                inputMode="numeric"
                autoComplete="off"
                maxLength={18}
                variant={errors.cnpj ? 'destructive' : 'default'}
                onChange={(event) => onFieldChange('cnpj', event.target.value)}
                data-testid="company-form-cnpj"
              />
              <FormFieldError>{errors.cnpj}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.slug)}>
              <FormFieldLabel>Slug</FormFieldLabel>
              <Input
                value={values.slug}
                readOnly={slugReadonly}
                aria-readonly={slugReadonly || undefined}
                className={slugReadonly ? 'cursor-not-allowed bg-muted' : undefined}
                onChange={(event) => onFieldChange('slug', event.target.value.toLowerCase())}
                data-testid={slugReadonly ? 'company-form-slug-readonly' : 'company-form-slug'}
              />
              {slugReadonly ? (
                <FormFieldHint>
                  🔒 Slug é a chave de integração da empresa e não pode ser alterado após a criação.
                </FormFieldHint>
              ) : null}
              <FormFieldError>{errors.slug}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.website)}>
              <FormFieldLabel>Site</FormFieldLabel>
              <Input
                value={values.website}
                placeholder="https://app.eureca.me/"
                inputMode="url"
                variant={errors.website ? 'destructive' : 'default'}
                onChange={(event) => onFieldChange('website', event.target.value)}
                data-testid="company-form-website"
              />
              <FormFieldError>{errors.website}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.primaryColor)}>
              <FormFieldLabel>Cor primária</FormFieldLabel>
              <div className="flex flex-row items-center gap-2">
                <Input
                  value={values.primaryColor}
                  placeholder="#1D8674"
                  variant={errors.primaryColor ? 'destructive' : 'default'}
                  onChange={(event) => onFieldChange('primaryColor', event.target.value)}
                  data-testid="company-form-primary-color"
                />
                <ColorInput
                  value={values.primaryColor}
                  invalid={Boolean(errors.primaryColor)}
                  onChange={(hex) => onFieldChange('primaryColor', hex)}
                  data-testid="company-form-primary-color-picker"
                />
              </div>
              <FormFieldError>{errors.primaryColor}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.logo)}>
              <FormFieldLabel>Logo da empresa</FormFieldLabel>
              <LogoFileInput
                fileName={logoFileName}
                hasError={Boolean(errors.logo)}
                onChange={onLogoChange}
              />
              <FormFieldHint>
                Envie em .JPG ou .PNG, com no máximo 5 MB e dimensão de 192 × 192 px. O fundo deve
                ter a cor primária, com a logo em aplicação positiva ou negativa.
              </FormFieldHint>
              <FormFieldError>{errors.logo}</FormFieldError>
            </FormField>

            {submitError ? (
              <p
                className="text-sm leading-5 text-destructive"
                role="alert"
                data-testid="company-form-submit-error"
              >
                {submitError}
              </p>
            ) : null}

            <div className="flex flex-row pt-3">
              <Button type="submit" loading={isSubmitting} data-testid="company-form-submit">
                Salvar alterações
              </Button>
            </div>
          </form>

          {children}
        </div>
      </main>
    </div>
  );
}

function StatusBadge({ status }: { status: CompanyEditStatus }) {
  if (status === 'active') {
    return (
      <Badge className="bg-secondary text-primary" leftIcon={<RadioIcon className="size-3" />}>
        Oportunidades ativas
      </Badge>
    );
  }
  return (
    <Badge
      className="bg-accent text-muted-foreground"
      leftIcon={<OctagonXIcon className="size-3" />}
    >
      Sem oportunidades
    </Badge>
  );
}

interface LogoFileInputProps {
  fileName: string | null;
  hasError: boolean;
  onChange: (file: File | null) => void;
}

function LogoFileInput({ fileName, hasError, onChange }: LogoFileInputProps) {
  return (
    <label
      className={cn(
        'flex h-9 w-full cursor-pointer flex-row items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm shadow-2xs transition-colors',
        'focus-within:ring-2 focus-within:ring-primary/40 hover:bg-muted/30',
        hasError ? 'border-destructive' : 'border-input'
      )}
    >
      <UploadIcon className="size-4 text-foreground" />
      <span className="font-medium text-foreground">Clique para selecionar outro arquivo</span>
      {fileName ? <span className="truncate text-muted-foreground">{fileName}</span> : null}
      <input
        type="file"
        accept="image/jpeg,image/png"
        className="sr-only"
        data-testid="company-form-logo"
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null;
          onChange(file);
        }}
      />
    </label>
  );
}
